// src/components/SimpleFormComponents/SimpleFormProvider.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useLayoutEffect,
  useCallback,
} from 'react';
import { Form, Button, Flex, Modal } from 'antd';
import appService from 'services/appService';
import { flattenName } from 'hooks/useFormElement';
import { isValidCpf, isValidCnpj } from 'utils/validation';
import { isValidCep, isValidCodigoPostal, mergeObjectsDeep, onProduction, returnOnlyNumbers } from 'utils/helpers';
import translateX from 'utils/translateX';
import addressService from 'services/address/addressService';
import postalPtService from 'services/address/postal-pt-external/postalPtService';
import { useNavigate } from 'react-router-dom';
import { useDocumentContext } from '../core/DocumentProvider';
import { useModuleContext } from '../core/ModuleProvider';
import useZustand from 'hooks/useZustand';
import { useModuleStore } from 'stores/module';
import appendZipState from 'utils/appendZipState';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// PERFORMANCE FIX: Stable empty array to prevent re-renders from default parameter
const EMPTY_ARRAY = [];

// *******************************************************************************
// CONTEXT
// *******************************************************************************
const SimpleFormContext = createContext({});
export const useSimpleFormContext = () => useContext(SimpleFormContext);

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
export const SimpleFormProvider = ({
  children,
  onFinish,
  onFinishError,
  formRef,
  initFocus,
  showSubmitButton = false,
  name = `simple-form-${Date.now()}`,
  layout = 'vertical',
  style,
  styleSubmitButton,
  isUpdating,
  additionalValues = {},
  requiredFields = EMPTY_ARRAY,
  requiredFieldsAtLeads = EMPTY_ARRAY,
  disabledFields = EMPTY_ARRAY,
  reloadEntirePage = false,
  locked = false,
  data: dataProp, // Data passed via props (for modal usage where storedCurrentPage is not available)
  useInModal = false,
  showInfoMessage = !useInModal,
  showWarningMessage = !useInModal,
  ignoreResetFormOnSubmit = false,
  forceViewAfterSave = true,
  formValidateTrigger = ['onBlur'],
  initialValues,
  isMock = false,
  onValuesChange: externalOnValuesChange, // External callback for form value changes
  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedCurrentPage = useZustand('module', 'currentPage');
  const setPageStore = useZustand('module', 'setPage');

  // Get navigateToFieldTab from DocumentContext for tab navigation on validation errors
  const { get, viewMode: mode, setViewMode: setMode, navigateToFieldTab } = useDocumentContext();
  const { registerProviderContext, actionRefreshed } = useModuleContext();
  const setGlobalStore = useZustand('global', 'setState');

  // IMPORTANT: Use zustand store for documentDuplicated instead of React context
  // This avoids stale closure issues when context doesn't propagate correctly
  const documentDuplicated = useZustand('module', 'documentDuplicated');
  const setDocumentDuplicatedStore = useZustand('module', 'setDocumentDuplicatedStore');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();
  const [formKey, setFormKey] = useState(+Date.now());
  const [modal, contextModal] = Modal.useModal();

  // IMPORTANT: When useInModal=true, parent component MUST wrap in <MemoryRouter>
  // Otherwise useNavigate will throw "must be used within <Router>" error
  const navigate = useNavigate();

  const dataRef = useRef(null);
  const dataInitizedRef = useRef(false);
  const justSubmittedRef = useRef(false); // Tracks if we just submitted to prevent form flash

  const additionalFieldsRef = useRef({});
  const initialValuesRef = useRef({}); // Store initial values to track changes

  // mirrors para leitura consistente no submit
  const zipActionsStateRef = useRef(null);

  // const searchingZipRef = useRef({});
  // const updatingCoordinatesRef = useRef({});
  // const pendingAddressNumberRef = useRef(null);
  // const foundCpfRef = useRef(null);
  // const foundZipRef = useRef(null);

  // controle de concorrência e cancelamento
  const coordinateTimerRef = useRef(null);
  const pendingGpsUpdateRef = useRef(null); // Queue for GPS updates when one is already running
  // const coordsOpIdRef = useRef(0);
  // const zipOpIdRef = useRef(0);

  // proxies para expor funções antes da definição real
  const submitFormRef = useRef(null);
  const resetFormRef = useRef(null);
  const submitFormProxy = useCallback((...args) => submitFormRef.current?.(...args), []);
  const resetFormProxy = useCallback((...args) => resetFormRef.current?.(...args), []);

  // CRITICAL: Ref to store the latest onFinish prop to avoid stale closure
  // This ensures handleFinish always calls the latest onFinish callback
  const onFinishRef = useRef(onFinish);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = appService.isMobile();
  // Use storedCurrentPage?.data for normal pages, or dataProp for modal usage
  const data = storedCurrentPage?.data || dataProp;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [working, setWorking] = useState(false);
  const [document, setDocument] = useState(null);

  const [hasError, setHasError] = useState(false);
  const [fieldsValidationStatus, setFieldsValidationStatus] = useState({});
  const [elRefs] = useState({});
  const [watchedFields, setWatchedFieldsState] = useState([]);
  const [watchedValues, setWatchedValues] = useState({});

  // PERFORMANCE FIX: Memoize Set for O(1) lookup in onValuesChange
  const watchedFieldsSet = useMemo(() => new Set(watchedFields), [watchedFields]);

  const [fieldsWithError, setFieldsWithError] = useState({});
  const [additionalFields, setAdditionalFields] = useState({});

  // cpf - aggregate by subdocumentField
  const [searchingCpf, setSearchingCpf] = useState({});
  // const [foundCpf, setFoundCpf] = useState({});

  // cnpj - aggregate by subdocumentField
  // const [searchingCnpj, setSearchingCnpj] = useState({});
  // const [foundCnpj, setFoundCnpj] = useState({});

  // uniqueEntity - stores entity found by unique document search (CNPJ/CPF)
  // Used to restore entity metadata on save if user chose to load existing document
  const [uniqueEntity, setUniqueEntityState] = useState(null);

  // zip - aggregate by subdocumentField
  // const [searchingZipState, setSearchingZipState] = useState({});
  // const [foundZipState, setFoundZipState] = useState({});
  // const [lastSearchedZipState, setLastSearchedZipState] = useState({}); // Track last searched ZIP (valid or invalid)

  const [formTmpState, setFormTmpState] = useState({});

  // gps coordinates - aggregate by subdocumentField
  const [waitingForAddressNumber, setWaitingForAddressNumber] = useState({});
  const [updatingCoordinates, setUpdatingCoordinates] = useState({});

  // const [manualAddressEntry, setManualAddressEntry] = useState(false); // Allow manual entry when API fails
  // guard para bloquear Save enquanto número está sujo ou resolvendo

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // CRITICAL: Keep onFinishRef updated with the latest onFinish prop
  // This fixes stale closure issues where handleFinish captures an old onFinish reference
  useEffect(() => {
    onFinishRef.current = onFinish;
  }, [onFinish]);

  // zip effects
  useEffect(() => {
    zipActionsStateRef.current = formTmpState;
  }, [formTmpState]);

  // additiona form fields effects
  useEffect(() => {
    additionalFieldsRef.current = additionalFields;
  }, [additionalFields]);

  useEffect(() => {
    // Skip if we just submitted with ignoreResetFormOnSubmit to prevent form flash
    if (justSubmittedRef.current && ignoreResetFormOnSubmit) return;
    if (!data || !initialValuesRef.current || dataInitizedRef.current) return;
    dataRef.current = data || initialValuesRef.current;
    dataInitizedRef.current = true;
  }, [data, initialValuesRef.current, ignoreResetFormOnSubmit]);

  // Capture initial form values when data or initialValues change
  // Skip if we just submitted with ignoreResetFormOnSubmit to prevent form flash
  useEffect(() => {
    if (justSubmittedRef.current && ignoreResetFormOnSubmit) {
      return; // Skip updating initialValuesRef after submit to prevent flash
    }
    const currentValues = form.getFieldsValue(true);
    initialValuesRef.current = JSON.parse(JSON.stringify(currentValues));
  }, [data, initialValues, formKey, form, ignoreResetFormOnSubmit]);

  useEffect(() => {
    // Skip reset when duplicating - documentDuplicated is set before mode changes to 'add'
    if (mode === 'add' && !documentDuplicated) {
      resetFormProxy();
      gotoInitFocus();
    }
  }, [actionRefreshed, mode, resetFormProxy, documentDuplicated]);

  // Focus initFocus field when switching from view to edit mode
  useEffect(() => {
    if (mode === 'edit' && !locked) {
      gotoInitFocus();
    }
  }, [mode, locked]);

  // Focus initFocus field on initial mount (for forms without DocumentProvider, e.g., auth pages)
  useEffect(() => {
    if (initFocus && mode === undefined) {
      gotoInitFocus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-validate form when requiredFields changes (e.g., user changes settings in parameters modal)
  // This clears validation errors for fields that are no longer required
  const prevRequiredFieldsRef = useRef(requiredFields);
  useEffect(() => {
    // Skip if requiredFields hasn't actually changed (reference equality check)
    const prevFields = prevRequiredFieldsRef.current;
    if (prevFields === requiredFields) return;

    // Skip if this is the first render (no previous value to compare)
    if (!prevFields || prevFields.length === 0) {
      prevRequiredFieldsRef.current = requiredFields;
      return;
    }

    // Update ref for next comparison
    prevRequiredFieldsRef.current = requiredFields;

    // Only re-validate if form was already submitted (user has seen validation errors)
    if (!submitted) return;

    // Find fields that were required but are no longer required
    const removedRequiredFields = prevFields.filter(field => !requiredFields.includes(field));

    if (removedRequiredFields.length > 0) {
      // Clear errors for fields that are no longer required
      setFieldsWithError(prev => {
        const updated = { ...prev };
        removedRequiredFields.forEach(field => {
          // Handle both exact match and nested field patterns (e.g., 'cnpj.value')
          Object.keys(updated).forEach(errorField => {
            if (errorField === field || errorField.startsWith(`${field}.`) || field.startsWith(`${errorField}.`)) {
              delete updated[errorField];
            }
          });
        });
        return updated;
      });

      // Re-validate form to update error state
      // Use setTimeout to ensure state updates have propagated
      setTimeout(async () => {
        try {
          await form.validateFields();
          setHasError(false);
        } catch (validationError) {
          // Form still has errors (other required fields are missing)
          // Update hasError based on remaining errors
          const hasRemainingErrors = validationError?.errorFields?.length > 0;
          setHasError(hasRemainingErrors);
        }
      }, 100);
    }
  }, [requiredFields, submitted, form]);

  // Debug helper: expõe função para capturar form state on-demand
  useEffect(() => {
    window.__CAPTURE_FORM_FOR_DEBUG__ = () => {
      const formValues = form.getFieldsValue(true);
      const setFormDebug = useZustand('module', 'setFormDebug', false);
      setFormDebug(formValues);
      return formValues;
    };

    return () => {
      delete window.__CAPTURE_FORM_FOR_DEBUG__;
    };
  }, [form]);

  // useEffect(() => {
  //   if (documentDuplicated) {
  //     // NOTE: We intentionally do NOT clear document/form data here
  //     // The duplicate action should keep form data intact so user can save as new document
  //     // Only clear the auxiliary states (CNPJ/CPF lookup results)
  //
  //     if (reloadEntirePage) {
  //       setFoundCnpj({});
  //       setFoundCpf({});
  //     }
  //
  //     // NOTE: We do NOT clear the documentDuplicated flag here anymore!
  //     // Clearing it would cause the RESET useEffect to re-run and reset the form.
  //     // The flag will be cleared when the document is saved or when navigating away.
  //   }
  //
  //   gotoInitFocus();
  // }, [initFocus, documentDuplicated, reloadEntirePage, isMobile]);

  // espelho em refs para leitura consistente no submit
  // useEffect(() => {
  //   pendingAddressNumberRef.current = waitingForAddressNumber;
  // }, [waitingForAddressNumber]);

  // useEffect(() => {
  //   updatingCoordinatesRef.current = updatingCoordinates;
  // }, [updatingCoordinates]);

  // useEffect(() => {
  //   searchingZipRef.current = searchingZip;
  // }, [searchingZip]);

  // useEffect(() => {
  //   foundCpfRef.current = foundCpf;
  // }, [foundCpf]);

  // useEffect(() => {
  //   foundZipRef.current = foundZipState;
  // }, [foundZipState]);

  // useLayoutEffect(() => {
  //   setGlobalStore('simpleFormProvider', { goFocus });
  // }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. INTERNAL FUNCTIONS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const gotoInitFocus = useCallback(() => {
    if (initFocus && !isMobile) {
      setTimeout(() => {
        elRefs[initFocus]?.focus();
      }, 200);
    }
  }, [initFocus, isMobile, elRefs]);

  // PERFORMANCE FIX: Memoize to prevent stableExposedValues from changing on every render
  const setFieldValidationStatus = useCallback((field, status) => {
    setFieldsValidationStatus(prev => ({
      ...prev,
      [flattenName(field)]: status,
    }));
  }, []);

  // PERFORMANCE FIX: Memoize to prevent stableExposedValues from changing on every render
  const setWatchFields = useCallback(fieldsArr => {
    let list = Array.isArray(fieldsArr) ? fieldsArr : [fieldsArr];
    list = list.map(f => flattenName(f));
    setWatchedFieldsState(prev => Array.from(new Set([...prev, ...list])));
  }, []);

  // PERFORMANCE FIX: Memoize to avoid creating new function on every render
  const removeFromFieldsWithError = useCallback(fieldId => {
    setFieldsWithError(prevState => {
      if (!prevState?.[fieldId]) return prevState;
      const { [fieldId]: _, ...rest } = prevState || {};
      return rest;
    });
  }, []);

  // Add multiple field errors at once and focus on first error field
  // Used by GenericModal for required fields validation
  const addFieldsWithError = useCallback(
    (errors, focusOnFirst = true) => {
      if (!errors || typeof errors !== 'object') return;

      // Set errors in fieldsWithError
      setFieldsWithError(prev => ({ ...prev, ...errors }));

      // Set hasError to trigger shouldShowErrors in context
      setHasError(true);

      // Focus on first errored field by DOM position (not registration order)
      if (focusOnFirst) {
        const errorFieldNames = Object.keys(errors);

        if (errorFieldNames.length > 0) {
          // Helper to find DOM element for a field (tries elRefs first, then DOM lookup)
          const findElement = fieldName => {
            // Try elRefs first
            if (elRefs[fieldName]) return elRefs[fieldName];

            // DOM fallback: look up by id or name attribute.
            // NOTE: use window.document — the local `document` state (line 146)
            // shadows the global DOM document and is null on a new record.
            const byId = window.document.getElementById(fieldName);
            if (byId) {
              // For Select, get the actual input inside
              const input = byId.tagName?.toLowerCase() === 'input' ? byId : byId.querySelector?.('input');
              return input || byId;
            }

            // Try by name attribute
            const byName = window.document.querySelector(`input[name="${fieldName}"]`);
            if (byName) return byName;

            // Try by AntD form item id pattern (ends with _fieldName)
            const container = window.document.querySelector(`[id$="_${fieldName}"]`);
            const nested = container?.querySelector?.('input');
            return nested || null;
          };

          // Build list of field elements with their names
          const fieldElements = errorFieldNames
            .map(fieldName => ({
              fieldName,
              element: findElement(fieldName),
            }))
            .filter(item => item.element);

          // Sort by DOM position (top to bottom, left to right)
          fieldElements.sort((a, b) => {
            const elA = a.element;
            const elB = b.element;
            if (!elA || !elB) return 0;

            // compareDocumentPosition returns bitmask
            // DOCUMENT_POSITION_FOLLOWING = 4: B follows A
            // DOCUMENT_POSITION_PRECEDING = 2: B precedes A
            const position = elA.compareDocumentPosition?.(elB);
            if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
            if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
            return 0;
          });

          const firstField = fieldElements[0];

          if (firstField) {
            // Navigate to tab if needed
            const tabChanged = navigateToFieldTab?.(firstField.fieldName);

            // Focus with delay
            const focusDelay = tabChanged ? 200 : 100;
            setTimeout(() => {
              const el = firstField.element;
              if (el?.focus) {
                el.focus();
                if (el?.scrollIntoView) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }
            }, focusDelay);
          }
        }
      }
    },
    [elRefs, navigateToFieldTab]
  );

  // const goFocus = (name, delay = 300) => {
  //   const flatName = flattenName(name);
  //   const el = elRefs[flatName];
  //
  //   if (el && el.focus) setTimeout(() => el.focus(), delay);
  // };
  const goFocus = useCallback(
    (name, fullSelection = false, delay = 300) => {
      const flatName = flattenName(name);
      const el = elRefs[flatName];

      if (el && el.focus)
        setTimeout(() => {
          el.focus();
          if (fullSelection) el.select();
          else el.setSelectionRange(el.value.length, el.value.length);
        }, delay);
    },
    [elRefs]
  );

  const getFieldError = errorFields => {
    let fieldWithError = Object.entries(fieldsValidationStatus).find(
      ([_, status]) => status === 'error'
    )?.[0];

    if (!fieldWithError && errorFields.length > 0) {
      fieldWithError = flattenName(errorFields[0].name);
    }

    return fieldWithError;
  };

  const handleFinishErrorInternal = async error => {
    setHasError(true);

    const errorFields = error?.errorFields || error;
    if (!errorFields) return onFinishError?.(error);

    // FIX: Populate fieldsWithError so components like SimpleCnpj, SimpleCpf can show error
    // Previously this was only done in submitForm, but onFinishFailed also needs it
    if (Array.isArray(errorFields)) {
      const newFieldsWithError = {};
      errorFields.forEach(err => {
        const fieldName = flattenName(err.name);
        // Use the first error message or 'required' as fallback
        newFieldsWithError[fieldName] = err.errors?.[0] || 'required';
      });

      if (Object.keys(newFieldsWithError).length > 0) {
        setFieldsWithError(prev => ({ ...prev, ...newFieldsWithError }));
      }
    }

    if (showWarningMessage) {
      await appService.message({ flag: 'w', text: 'incomplete_data', key: 'saveContext' });
    }

    let fieldError = getFieldError(errorFields);
    if (fieldError) {
      // Navigate to the tab containing the error field (if tabs are used)
      const tabChanged = navigateToFieldTab?.(fieldError);

      // Focus on the error field after tab navigation (with delay if tab changed)
      const focusDelay = tabChanged ? 200 : 100;
      setTimeout(() => {
        const el = elRefs[fieldError];
        if (el?.focus) {
          el.focus();
          // Scroll the field into view for better visibility
          el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        }
      }, focusDelay);
    }

    onFinishError?.(error);
  };

  const submitForm = useCallback(async () => {
    appService.notificationDestroy('uniqueCepNotificationKey');
    appService.notificationDestroy('cepApiErrorNotificationKey');

    try {
      setSubmitted(true);

      // leitura consistente via refs para evitar stale-closure
      // const isNumberPending = pendingAddressNumberRef.current != null;
      // const isCoordsUpdating = updatingCoordinatesRef.current;
      // const isCoordsUpdating = getZipState('updatingCoordinates');
      const isCoordsUpdating = false;

      // if (isNumberPending || isCoordsUpdating) {
      if (isCoordsUpdating) {
        if (showWarningMessage) {
          await appService.message({
            flag: 'w',
            text: 'wait_address_validation',
            key: 'saveContext',
          });
        }
        return;
      }

      const erroredFields = new Set();
      const newFieldsWithError = {};

      // Validate OR groups (e.g., 'phone.value|email.value' means at least one must be filled)
      const orGroups = requiredFields.filter(f => f.includes('|'));
      if (orGroups.length > 0) {
        const allValues = form.getFieldsValue(true);
        const flatValues = flattenObject(allValues);

        for (const orGroup of orGroups) {
          const fieldNames = orGroup.split('|').map(f => f.trim());
          // Check if at least one field in the OR group has a value
          const hasValue = fieldNames.some(fieldName => {
            const value = flatValues[fieldName];
            return value !== undefined && value !== null && value !== '';
          });

          if (!hasValue) {
            // None of the OR fields have value - add error to ALL fields in the group
            fieldNames.forEach(fieldName => {
              erroredFields.add(fieldName);
              newFieldsWithError[fieldName] = translateX('at_least_one_required');
            });
          }
        }
      }

      try {
        await form.validateFields();
      } catch (validationError) {
        if (validationError?.errorFields?.length) {
          validationError.errorFields.forEach(err => {
            const fieldName = flattenName(err.name);
            erroredFields.add(fieldName);
            // Set error message for components that use fieldsWithError (SimpleCnpj, SimpleCpf, etc.)
            newFieldsWithError[fieldName] = err.errors?.[0] || 'required';
          });
        }
      }

      // Update fieldsWithError so components can show the error
      if (Object.keys(newFieldsWithError).length > 0) {
        setFieldsWithError(prev => ({ ...prev, ...newFieldsWithError }));
      }

      Object.entries(fieldsValidationStatus).forEach(([field, status]) => {
        if (status === 'error') {
          erroredFields.add(flattenName(field));
        }
      });

      if (erroredFields?.size > 0) {
        setHasError(true);

        let firstErroredField = Object?.keys(elRefs)?.find(field => {
          return erroredFields.has(flattenName(field));
        });

        if (firstErroredField) {
          // Navigate to the tab containing the error field (if tabs are used)
          const tabChanged = navigateToFieldTab?.(firstErroredField);

          // Focus on the error field after tab navigation (with delay if tab changed)
          const focusDelay = tabChanged ? 200 : 100;
          setTimeout(() => {
            const el = elRefs[firstErroredField];
            if (el?.focus) {
              el.focus();
              // Scroll the field into view for better visibility
              el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
            }
          }, focusDelay);
        }

        if (showWarningMessage) {
          await appService.message({ flag: 'w', text: 'incomplete_data', key: 'saveContext' });
        }

        return;
      }

      const formValues = await form.getFieldsValue(true);

      await handleFinish(formValues);
    } catch (error) {
      await handleFinishErrorInternal(error);
    }
  }, [form, fieldsValidationStatus, elRefs, showWarningMessage]);

  // conecta a função real ao proxy após definida
  useEffect(() => {
    submitFormRef.current = submitForm;
  }, [submitForm]);

  const validateForm = useCallback(
    async fields => {
      if (!form) return { valid: false, errors: null };

      try {
        if (Array.isArray(fields) && fields.length) {
          await form.validateFields(fields);
        } else {
          await form.validateFields();
        }

        return { valid: true, errors: null };
      } catch (error) {
        setHasError(true);

        const errorFields = error?.errorFields || [];
        const flatFieldName = flattenName(errorFields?.[0]?.name);

        form.setFields(
          errorFields.map(({ name, errors }) => {
            const flatFieldName2 = flattenName(name);
            return {
              name: flatFieldName2,
              errors,
              touched: true,
            };
          })
        );

        goFocus(flatFieldName);

        return { valid: false, errors: errorFields };
      }
    },
    [form, goFocus]
  );

  const resetForm = useCallback(() => {
    setHasError(false);
    setSubmitted(false);
    setSubmitting(false);
    form.resetFields();
    setFormKey(+Date.now());
    setWorking(false);
    setFormTmpState({}); // Reset form state inline instead of using resetFormState
    gotoInitFocus();
    // setFunctions?.({ avatarInitial: '' });

    // Reset initial values ref
    setTimeout(() => {
      const currentValues = form.getFieldsValue(true);
      initialValuesRef.current = JSON.parse(JSON.stringify(currentValues));
    }, 0);
  }, [form, gotoInitFocus]); // setFunctions is stable from context

  useEffect(() => {
    resetFormRef.current = resetForm;
  }, [resetForm]);

  // Clear validation error state (for step modals that need to reset between steps)
  const clearValidationState = useCallback(() => {
    setHasError(false);
  }, []);

  // expõe funções para quem passou formRef (após todas as definições)
  useEffect(() => {
    const refPayload = {
      submitForm: submitFormProxy,
      resetForm: resetFormProxy,
      form,
      validateForm,
      goFocus,
      clearValidationState,
    };

    if (typeof formRef === 'function') formRef(refPayload);
    else if (formRef?.current !== undefined) formRef.current = refPayload;
  }, [formRef, form, submitFormProxy, resetFormProxy, validateForm, goFocus, clearValidationState]);

  const handleFinish = async formValuesParam => {
    // CRITICAL: Use ref to get the latest onFinish callback
    // This avoids stale closure issues when items/payments change in parent
    const currentOnFinish = onFinishRef.current;
    if (!currentOnFinish) return;

    setHasError(false);
    if (showInfoMessage) {
      await appService.message({ flag: 'i', text: 'saving_data', key: 'saveContext' });
    }

    let formattedAdditionalFields = {};
    const af = additionalFieldsRef.current;

    Object.keys(af).map(key => {
      const value = af[key];
      const fieldArr = key.includes('.') ? key.split('.') : key;
      const data2 = Array.isArray(fieldArr)
        ? fieldArr.reduceRight((acc, key2) => ({ [key2]: acc }), value)
        : { [key]: value };
      formattedAdditionalFields = mergeObjectsDeep(formattedAdditionalFields, data2);
    });

    const formValues = formValuesParam ?? form.getFieldsValue(true);

    // Use refs para garantir valores mais recentes (evitar stale closure)
    // const currentFoundZip = foundZipRef.current;
    // const currentFoundCpf = foundCpfRef.current;

    const zipState = zipActionsStateRef.current;
    const initialValues = initialValuesRef.current;

    // Check if this is a new form (no initial data from database)
    const hasInitialData = dataRef?.current && Object.keys(dataRef.current).length > 0;

    // Calculate changed fields by comparing RAW values (without zipState decorations)
    // This prevents zipState decorations from being detected as user changes
    const valuesChangedRaw = getChangedFields(
      formValues, // Compare raw form values
      dataRef?.current || initialValuesRef.current || {} // Use initialValuesRef (updated when form loads), not initialValues prop (initial mocks)
    );

    // Apply zipState decorations ONLY to changed fields
    const valuesChanged = appendZipState(valuesChangedRaw, zipState);

    // Apply zipState decorations to ALL form values for allValues
    const formValuesWithZipState = appendZipState(formValues, zipState);


    // IMPORTANT: Use multiple fallbacks to ensure we have ALL document data:
    // 1. dataRef.current - captured when document was first loaded
    // 2. data - current store data (fallback if dataRef wasn't set, e.g., after component remount)
    // 3. initialValues - form values at init time
    // 4. {} - empty object as last resort
    const allValues = {
      ...(dataRef?.current || data || initialValues || {}),
      ...formValuesWithZipState, // Include current form values with zipState decorations
      ...formattedAdditionalFields, // Additional fields override everything
    };

    // CRITICAL: Access documentDuplicated directly from store to avoid stale closure
    // The submitForm useCallback doesn't include handleFinish in its dependencies,
    // so it captures a stale version of handleFinish with old documentDuplicated value
    const { documentDuplicated: documentDuplicatedFromStore, setDocumentDuplicatedStore } =
      useModuleStore.getState();

    try {
      setSubmitting(true);

      // IMPORTANT: For new forms (SignIn, SignUp, etc) with no initial data,
      // always use formValues to include all fields (including password)
      // For existing documents, use valuesChanged for minimal updates
      // For DUPLICATED documents, use all form values (excluding _id and _metadata)
      let minimalValues;


      if (documentDuplicatedFromStore) {
        // Duplicated document: send ALL values from allValues (this is a new document with copied data)
        // Exclude _id and _metadata to ensure POST creates a new document
        // Use allValues because it has ALL fields (from dataRef.current + formValues + additionalFields)
        const { _id, _metadata, ...allValuesWithoutId } = allValues;
        minimalValues = allValuesWithoutId;
      } else if (isMock === false || onProduction()) {
        minimalValues = hasInitialData
          ? valuesChanged || formValues || allValues
          : formValuesWithZipState; // New form: use all form values
      } else {
        minimalValues = allValues;
      }

      const values = { ...additionalValues, ...minimalValues, ...formattedAdditionalFields };

      const result = await currentOnFinish({
        values,
        allValues,
        uniqueEntity, // Pass unique entity for restoration on save
      });


      // If onFinish returns false, abort the save process and don't change viewMode
      if (result === false) {
        return;
      }

      // Clear documentDuplicated flag after successful save (done=true clears the flag)
      // This ensures subsequent saves work normally (only send changed fields)
      if (documentDuplicatedFromStore) {
        setDocumentDuplicatedStore?.(true);
      }


      // Set flag to prevent form flash when data updates in store
      if (ignoreResetFormOnSubmit) {
        justSubmittedRef.current = true;
        // Clear the flag after store update propagates (500ms should be enough)
        setTimeout(() => {
          justSubmittedRef.current = false;
        }, 500);
      }


      // Check viewModeAfterSave setting from config
      // - 'edit': stay in edit mode after save
      // - 'view' or undefined: fall back to forceViewAfterSave prop behavior (default: switch to 'view')
      const viewModeAfterSave = storedCurrentPage?.settings?.viewModeAfterSave;
      const shouldSwitchToView = viewModeAfterSave === 'edit' ? false : forceViewAfterSave;

      if (!useInModal && shouldSwitchToView) {
      // if (shouldSwitchToView) {


        // Se tem tabs (usando DocumentProvider) ou é layout document, volta para 'view'
        const hasTabs = storedCurrentPage?.settings?.initialTab || storedCurrentPage?.tabs;


        if (storedCurrentPage?.layout === 'document' || hasTabs) {
          setMode?.('view');
        } else {
          setMode?.(null);
        }
      }
    } catch (err) {
      // Silently handle validation errors (thrown to prevent viewMode change)
      if (err?.message !== 'VALIDATION_FAILED') {
        console.error('SimpleFormProvider handleFinish error:', err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // PERFORMANCE FIX: Memoize to keep stable reference
  const formatAddress = useCallback(doc => {
    return {
      address1: doc?.address1,
      addressAdditionalInfo: doc?.addressAdditionalInfo,
      addressCoordinates: doc?.addressCoordinates,
      addressMetadata: doc?.addressMetadata,
      addressTitle: doc?.addressTitle,
      city: doc?.city,
      erro: doc?.erro,
      neighborhood: doc?.neighborhood,
      number: doc?.number,
      region: doc?.region,
      zip: doc?.zip,
      __city: doc?.__city,
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // CPF
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Handle CPF field blur
   * Note: CPF validation is handled by SimpleCpf using useCustomValidationStatus
   * This handler only performs CPF data search if searchCpf flag is enabled
   *
   * 1. Searches CPF data using provider (getCpfData) - controlled by searchCpf flag
   *    - Provider 'jarvisly': searches in database
   *    - Future providers: Receita Federal, etc.
   * 2. Exposes found data via console.log for now
   * 3. Always allows moving to next field (form validation will prevent submit if invalid)
   */

  // PERFORMANCE FIX: Memoize to prevent stableExposedValues from changing on every render
  const setFormCpfState = useCallback((field, state) => {
    if (!field) return console.error(`setFormCpfState: 'field' flatten name in params is required`);
    if (typeof state !== 'object') {
      return console.error(
        `setCpfState: 'state' params need be an object with an 'action' key, got: ${state} (${typeof state})`
      );
    }

    setFormTmpState(p => ({
      ...p,
      cpf: { ...(p?.cpf || {}), [field]: { ...(p?.[field] || {}), ...state } },
    }));
  }, []);

  // PERFORMANCE FIX: Use ref for reading state to avoid stale closure while keeping stable reference
  const getFormCpfState = useCallback((field, key = null) => {
    const currentState = zipActionsStateRef.current;
    if (!field) return currentState;

    const fieldState = currentState?.cpf?.[field];
    if (!fieldState) return null;

    return key ? fieldState?.[key] : fieldState;
  }, []);

  // PERFORMANCE FIX: Memoize to keep stable reference
  const handleCpfChange = useCallback(
    ({ ev, cpfFlattenFieldName }) => {
      const field = cpfFlattenFieldName || ev?.target?.id;
      removeFromFieldsWithError(field);
    },
    [removeFromFieldsWithError]
  );

  const handleCpfBlur = async ({
    ev,
    value,
    flattenParentFieldName,
    fullParentFieldName,
    searchCpf = true,

    cpf: {
      flattenFieldName: cpfFlattenFieldName,
      fullFieldName: cpfFullFieldName,
      focus: cpfFocus,
    },
  }) => {
    appService.notificationDestroy('cpfApiErrorNotificationKey');

    // -------------------------------------------------------------------- //
    // 1. mapping
    // -------------------------------------------------------------------- //
    const field = cpfFlattenFieldName || ev?.target.id;
    const cpf = returnOnlyNumbers(value);
    const set = state => setFormCpfState(field, state);
    const get = key => getFormCpfState(field, key);

    // references mapping
    const searching = get('searching');
    const lastSearchFound = get('lastSearchFound');

    // -------------------------------------------------------------------- //
    // 2. validation
    // -------------------------------------------------------------------- //
    // validate actions in the process
    if (!isValidCpf(cpf) || searching) return false;

    // Check if this CPF was already searched (valid or invalid)
    if (cpf === lastSearchFound) return true;

    // -------------------------------------------------------------------- //
    // 3. process the CEP searching...
    // -------------------------------------------------------------------- //
    setWorking?.(true);
    set({ searching: true, apiFailure: false });

    await appService.message({ flag: 'i', text: 'searching_cpf', key: 'searchCpf', duration: 0 });
    const result = await addressService.getCpfData(cpf);
    appService.messageDestroy('searchCpf');

    // -------------------------------------------------------------------- //
    // 4. handle result cases
    // -------------------------------------------------------------------- //
    if (result.case === 1) {
      // API is Broken - manual edit
      set({ apiFailure: true, searching: false });
      setWorking?.(false);
    } else if (result.case === 2) {
      // CPF not found in database - allow manual fill (don't block form)
      // Show informative notification but don't set field as error
      // User can still fill the form manually and save
      set({ apiFailure: false, searching: false, lastSearchFound: cpf, notFound: true });
      setWorking?.(false);
    } else if (result.case === 3) {
      // CPF is valid - accepted

      const doc = result.data;

      set({
        apiFailure: false,
        searching: false,
        data: doc,
        lastSearchFound: cpf,
      });

      setWorking?.(false);

      // Populate form with address data (including coordinates if found)
      // if (fullParentFieldName) {
      //   form.setFieldValue(fullParentFieldName, doc);
      // } else {
      //   form.setFieldsValue(doc);
      // }
      // if (doc?.addressMetadata?.cpfWithMultipleAddress) {
      //   addressFocus?.();
      // } else {
      //   numberFocus?.();
      // }
    }

    /*    try {
      // =========================================================================
      // Get CPF data using provider (only if CPF is valid)
      // =========================================================================
      const cpfDataResult = await addressService.getCpfData(cleanCpf);

      if (cpfDataResult.case === 3) {
        // CPF found - expose data via console.log
        const cpfData = cpfDataResult.data;
        console.log('✅ CPF data found:', cpfData);

        // Save to state
        setFoundCpf(cpfData);

        // Move to next field
        setSearchingCpf(false);
        setWorking?.(false);
        return true;
      } else if (cpfDataResult.case === 2) {
        // CPF not found - just log and move on
        console.log('⚠️ CPF not found:', cpfDataResult.error);

        setSearchingCpf(false);
        setWorking?.(false);
        return true;
      } else {
        // API failure - log and move on
        console.log('❌ CPF service failed:', cpfDataResult.error);

        setSearchingCpf(false);
        setWorking?.(false);
        return true;
      }
    } catch (error) {
      console.error('❌ Unexpected error in handleCpfBlur:', error);
      setSearchingCpf(false);
      setWorking?.(false);
      return true;
    }*/
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // CNPJ
  // ─────────────────────────────────────────────────────────────────────────────
  // PERFORMANCE FIX: Memoize to prevent stableExposedValues from changing on every render
  const setFormCnpjState = useCallback((field, state) => {
    if (!field)
      return console.error(`setFormCnpjState: 'field' flatten name in params is required`);
    if (typeof state !== 'object') {
      return console.error(
        `setCnpjState: 'state' params need be an object with an 'action' key, got: ${state} (${typeof state})`
      );
    }

    setFormTmpState(p => ({
      ...p,
      cnpj: { ...(p?.cnpj || {}), [field]: { ...(p?.cnpj?.[field] || {}), ...state } },
    }));
  }, []);

  // PERFORMANCE FIX: Use ref for reading state to avoid stale closure while keeping stable reference
  const getFormCnpjState = useCallback((field, key = null) => {
    const currentState = zipActionsStateRef.current;
    if (!field) return currentState;

    const fieldState = currentState?.cnpj?.[field];
    if (!fieldState) return null;

    return key ? fieldState?.[key] : fieldState;
  }, []);

  // PERFORMANCE FIX: Memoize to keep stable reference
  const handleCnpjChange = useCallback(
    ({ ev, cnpjFlattenFieldName }) => {
      const field = cnpjFlattenFieldName || ev?.target?.id;
      removeFromFieldsWithError(field);
    },
    [removeFromFieldsWithError]
  );

  const handleCnpjBlur = async ({
    ev,
    value,
    flattenParentFieldName,
    fullParentFieldName,
    searchCnpj = true,

    cnpj: {
      flattenFieldName: cnpjFlattenFieldName,
      fullFieldName: cnpjFullFieldName,
      focus: cnpjFocus,
    },
  }) => {
    appService.notificationDestroy('cnpjApiErrorNotificationKey');

    // -------------------------------------------------------------------- //
    // 1. mapping
    // -------------------------------------------------------------------- //
    const field = cnpjFlattenFieldName || ev?.target.id;
    const cnpj = returnOnlyNumbers(value);
    const set = state => setFormCnpjState(field, state);
    const get = key => getFormCnpjState(field, key);

    // references mapping
    const searching = get('searching');
    const lastSearchFound = get('lastSearchFound');

    // -------------------------------------------------------------------- //
    // 2. validation
    // -------------------------------------------------------------------- //
    // validate actions in the process
    if (!isValidCnpj(cnpj) || searching) return false;

    // Check if this CNPJ was already searched (valid or invalid)
    if (cnpj === lastSearchFound) return true;

    // -------------------------------------------------------------------- //
    // 3. process the CNPJ searching...
    // -------------------------------------------------------------------- //
    setWorking?.(true);
    set({ searching: true, apiFailure: false });

    await appService.message({ flag: 'i', text: 'searching_cnpj', key: 'searchCnpj', duration: 0 });
    const result = await addressService.getCnpjData(cnpj);
    appService.messageDestroy('searchCnpj');

    // -------------------------------------------------------------------- //
    // 4. handle result cases
    // -------------------------------------------------------------------- //
    if (result.case === 1) {
      // API is Broken - manual edit
      set({ apiFailure: true, searching: false });
      setWorking?.(false);
    } else if (result.case === 2) {
      // CNPJ is invalid - not accepted
      setFieldsWithError(prevState => ({
        ...prevState,
        [field]: 'cnpj_not_found',
      }));

      set({ apiFailure: false, searching: false, lastSearchFound: cnpj });
      setWorking?.(false);

      cnpjFocus?.(true);
    } else if (result.case === 3) {
      // CNPJ is valid - accepted

      const doc = result.data;

      set({
        apiFailure: false,
        searching: false,
        data: doc,
        lastSearchFound: cnpj,
      });

      setWorking?.(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UNIQUE ENTITY (for unique document validation)
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Set the unique entity found by document search
   * This entity will be used in handleFinish to restore metadata if saving
   * Also calls external setUniqueEntity callback if provided via props (for modal usage)
   * @param {Object|null} entity - The entity data or null to clear
   * @param {string} entity._id - Entity ID
   * @param {string} entity.documentKey - Document type used to find (e.g., 'cnpj')
   * @param {string} entity.documentValue - Document value used to find
   * @param {Object} entity.data - Full entity data
   */
  const setUniqueEntity = useCallback(entity => {
    // Update internal state
    if (entity === null) {
      setUniqueEntityState(null);
    } else {
      setUniqueEntityState({
        _id: entity._id,
        documentKey: entity.documentKey,
        documentValue: entity.documentValue,
        data: entity.data,
      });
    }

    // Also call external callback if provided (for modal usage like EditEntityModal)
    // This allows parent components to sync their own uniqueEntity state
    if (typeof props.setUniqueEntity === 'function') {
      props.setUniqueEntity(entity?.data || entity);
    }
  }, [props.setUniqueEntity]);

  /**
   * Get the current unique entity state
   * @returns {Object|null} The unique entity data or null
   */
  const getUniqueEntity = useCallback(() => {
    return uniqueEntity;
  }, [uniqueEntity]);

  // ─────────────────────────────────────────────────────────────────────────────
  // ZIP
  // ─────────────────────────────────────────────────────────────────────────────
  const resetFormState = category => {
    if (category) {
      setFormTmpState({ [category]: {} });
    } else {
      setFormTmpState({});
    }
  };

  // PERFORMANCE FIX: Memoize to prevent stableExposedValues from changing on every render
  const setFormZipState = useCallback((field, state) => {
    if (!field) return console.error(`setFormZipState: 'field' flatten name in params is required`);
    if (typeof state !== 'object') {
      return console.error(
        `setZipState: 'state' params need be an object with an 'action' key, got: ${state} (${typeof state})`
      );
    }

    setFormTmpState(p => ({
      ...p,
      zip: { ...(p?.zip || {}), [field]: { ...(p?.zip?.[field] || {}), ...state } },
    }));
  }, []);

  // PERFORMANCE FIX: Use ref for reading state to avoid stale closure while keeping stable reference
  const getFormZipState = useCallback((field, key = null) => {
    const currentState = zipActionsStateRef.current;
    if (!field) return currentState;

    const fieldState = currentState?.zip?.[field];
    if (!fieldState) return null;

    return key ? fieldState?.[key] : fieldState;
  }, []);

  // PERFORMANCE FIX: Memoize to keep stable reference
  const handleAddressZipChange = useCallback(
    ({ ev, zipFlattenFieldName }) => {
      const field = zipFlattenFieldName || ev?.target?.id;
      removeFromFieldsWithError(field);
    },
    [removeFromFieldsWithError]
  );

  const handleAddressZipBlur = async ({
    ev,
    value,
    flattenParentFieldName,
    fullParentFieldName,

    zip: {
      fullFieldName: zipFullFieldName,
      flattenFieldName: zipFlattenFieldName,
      focus: zipFocus,
    },
    address: { focus: addressFocus },
    number: { fullFieldName: numberFullFieldName, focus: numberFocus },
  }) => {
    appService.notificationDestroy('cepApiErrorNotificationKey');
    appService.notificationDestroy('cepNotFoundNotificationKey');

    // -------------------------------------------------------------------- //
    // 1. mapping
    // -------------------------------------------------------------------- //
    const field = zipFlattenFieldName || ev?.target.id;
    const zip = returnOnlyNumbers(value);
    const set = state => setFormZipState(field, state);
    const get = key => getFormZipState(field, key);

    // references mapping
    const searching = get('searching');
    const lastSearchFound = get('lastSearchFound');
    const updatingCoordinates = get('updatingCoordinates');

    // -------------------------------------------------------------------- //
    // 2. validation
    // -------------------------------------------------------------------- //
    // Resolve the address country to pick the right validator + lookup API.
    // BR -> CEP (8 digits, ViaCEP); PT -> Codigo Postal (7 digits, GeoAPI.pt);
    // any other country has no lookup API (manual entry only).
    const parentPath = Array.isArray(fullParentFieldName)
      ? fullParentFieldName
      : fullParentFieldName
        ? [fullParentFieldName]
        : [];
    const addressCountry = String(
      form?.getFieldValue([...parentPath, 'countryCode']) ||
        form?.getFieldValue('countryCode') ||
        'BR'
    ).toUpperCase();
    const isPtZip = addressCountry === 'PT';
    const isBrZip = addressCountry === 'BR';

    // No lookup provider for other countries -> allow manual entry, skip search
    if (!isBrZip && !isPtZip) {
      return false;
    }

    const isValidZip = isPtZip ? isValidCodigoPostal(zip) : isValidCep(zip);

    // validate actions in the process
    if (!isValidZip || searching || updatingCoordinates) {
      return false;
    }

    // Check if this ZIP was already searched (valid or invalid)
    if (zip === lastSearchFound) {
      return true;
    }

    // -------------------------------------------------------------------- //
    // 3. process the CEP searching...
    // -------------------------------------------------------------------- //
    setWorking?.(true);
    set({ searching: true, apiFailure: false });

    // 53990-000 - multi cep - Rua Eurico Cavalcanti de Albuquerque
    // 53990-001 - nao existe
    // 13010-111 - unico cep
    await appService.message({ flag: 'i', text: 'searching_cep', key: 'searchCep', duration: 0 });
    const result = isPtZip
      ? await postalPtService.getPortugalPostalCode(zip)
      : await addressService.getBrazilCep(zip);
    appService.messageDestroy('searchCep');

    // -------------------------------------------------------------------- //
    // 4. handle result cases
    // -------------------------------------------------------------------- //
    if (result.case === 1) {
      // API is Broken - manual edit
      set({ apiFailure: true, searching: false });
      setWorking?.(false);

      // Clear pending GPS queue (no coordinates available without CEP data)
      pendingGpsUpdateRef.current = null;

      addressFocus?.();
    } else if (result.case === 2) {
      // CEP is invalid - not accepted
      setFieldsWithError(prevState => ({
        ...prevState,
        [field]: 'zip_not_found',
      }));

      set({ apiFailure: false, searching: false, lastSearchFound: zip });
      setWorking?.(false);

      // Clear pending GPS queue (no coordinates available for invalid CEP)
      pendingGpsUpdateRef.current = null;

      zipFocus?.(true);
    } else if (result.case === 3) {
      // CEP is valid - accepted

      const doc = result.data;

      // Fetch GPS coordinates automatically if we have a complete address
      // Skip if zipWithMultipleAddress (user needs to fill address manually first)
      let initialGpsHash = null;
      if (!doc.addressMetadata?.zipWithMultipleAddress && doc.address1 && doc.city && doc.region) {
        const gpsResult = await addressService.getGpsCoordinates({
          address: doc.address1,
          number: '', // Number will be filled by user later
          city: doc.city,
          region: doc.region,
        });

        // If coordinates found, add to doc
        if (gpsResult.case === 3) {
          doc.addressCoordinates = gpsResult.data; // [lat, lng]
          // Create hash for this initial GPS search (without number)
          initialGpsHash = `${doc.zip || ''}|${doc.address1 || ''}|`;
        }
      }

      set({
        apiFailure: false,
        searching: false,
        data: doc,
        lastSearchFound: zip,
        zipWithMultipleAddress: !!doc?.addressMetadata?.zipWithMultipleAddress,
        lastGpsSearched: initialGpsHash, // Save hash to prevent duplicate GPS searches
      });

      setWorking?.(false);

      // Populate form with address data (including coordinates if found)
      // IMPORTANT: Detect if this is a CEP change (zipState.data exists) or first search
      // - CEP change: Replace address fields but preserve user-specific fields (number, address2, etc)
      // - First search: Preserve all user-entered fields
      const isCepChange = !!get('data')?.zip;

      if (fullParentFieldName) {
        const currentValues = form.getFieldValue(fullParentFieldName) || {};
        const mergedValues = isCepChange
          ? mergeReplacingAddressData(currentValues, doc)
          : mergePreservingUserData(currentValues, doc);
        form.setFieldValue(fullParentFieldName, mergedValues);
      } else {
        const currentValues = form.getFieldsValue(true);
        const mergedValues = isCepChange
          ? mergeReplacingAddressData(currentValues, doc)
          : mergePreservingUserData(currentValues, doc);
        form.setFieldsValue(mergedValues);
      }

      // Check if there's a pending GPS update queued (race condition handling)
      // This happens when user types number during CEP search and blurs before CEP finishes
      if (pendingGpsUpdateRef.current) {
        const pending = pendingGpsUpdateRef.current;
        pendingGpsUpdateRef.current = null; // Clear queue before executing

        // CRITICAL: Merge CEP data (doc) with queued data (number)
        // We can't rely on zipState because setFormZipState hasn't updated yet (React async state)
        const mergedAddressData = {
          ...doc, // CEP data (address1, city, region, zip, etc)
          ...pending.addressData, // Queued data (number: "22")
        };

        // Execute the pending update with merged data
        // Pass all component dependencies + lastSearchFound to preserve it (React state is async)
        await updateGpsCoordinates(pending.zipFlattenFieldNameParent, mergedAddressData, {
          getFormZipState,
          setFormZipState,
          setWorking,
          addressService,
          form,
          pendingGpsUpdateRef,
          lastSearchFound: zip,
        });
      }

      // 53990-000
      if (doc?.addressMetadata?.zipWithMultipleAddress) {
        addressFocus?.();
      } else {
        // numberFocus?.();
      }
    }
  };

  /**
   * Handle number field change with debounce
   * Updates GPS coordinates after 2 seconds of inactivity
   */
  const handleAddressNumberChange = ({
    ev,
    value,
    flattenParentFieldName,
    fullParentFieldName,

    zip: { flattenFieldName: zipFlattenFieldName },
    number: { flattenFieldName: numberFlattenFieldName },
  }) => {
    const incoming = String(value ?? '');
    // Use the same key that handleAddressZipBlur uses to save state
    const zipState = getFormZipState(zipFlattenFieldName);
    const current = String(zipState?.data?.number ?? '');

    // If no address data, skip
    if (!zipState?.data?.address1 && !zipState?.data?.city) {
      return;
    }

    // If number hasn't changed, cancel debounce
    if (incoming === current) {
      if (coordinateTimerRef.current) {
        clearTimeout(coordinateTimerRef.current);
        coordinateTimerRef.current = null;
      }
      return;
    }

    // Cancel previous debounce
    if (coordinateTimerRef.current) {
      clearTimeout(coordinateTimerRef.current);
      coordinateTimerRef.current = null;
    }

    // Start 2-second debounce to save state and update coordinates
    coordinateTimerRef.current = setTimeout(async () => {
      const set = state => setFormZipState(zipFlattenFieldName, state);
      const updatedData = {
        ...zipState?.data,
        number: incoming,
      };
      set({ data: updatedData });

      await updateGpsCoordinates(zipFlattenFieldName, updatedData, {
        getFormZipState,
        setFormZipState,
        setWorking,
        addressService,
        form,
        pendingGpsUpdateRef,
      });
    }, 2000);
  };

  /**
   * Handle number field blur
   * Updates GPS coordinates immediately if number changed
   */
  const handleAddressNumberBlur = async ({
    ev,
    value,
    flattenParentFieldName,
    fullParentFieldName,

    zip: { flattenFieldName: zipFlattenFieldName },
    number: { flattenFieldName: numberFlattenFieldName },
  }) => {
    const incoming = String(value ?? '');
    // Use the same key that handleAddressZipBlur uses to save state
    const zipState = getFormZipState(zipFlattenFieldName);

    // Cancel any pending debounce
    if (coordinateTimerRef.current) {
      clearTimeout(coordinateTimerRef.current);
      coordinateTimerRef.current = null;
    }

    // CRITICAL: Check if CEP search or GPS update is already running FIRST
    // This handles race condition when user types number during CEP search
    // (when zipState.data is still empty because CEP hasn't finished yet)
    if (zipState?.searching || zipState?.updatingCoordinates) {
      const currentNumber = String(zipState?.data?.number ?? '');

      // Only queue if number actually changed
      if (incoming !== currentNumber) {
        // Save number to state first
        const set = state => setFormZipState(zipFlattenFieldName, state);
        const updatedData = {
          ...zipState?.data,
          number: incoming,
        };
        set({ data: updatedData });

        // Queue this update to run after current process finishes
        pendingGpsUpdateRef.current = {
          zipFlattenFieldNameParent: zipFlattenFieldName,
          addressData: updatedData,
        };
      }

      return;
    }

    // If no address data, skip (only check this AFTER checking for running processes)
    // This also handles the case where data was loaded from database (no ZIP search was done)
    if (!zipState?.data?.address1 && !zipState?.data?.city) {
      return;
    }

    // Check if number has actually changed from the saved value
    // This prevents GPS updates when user just navigates through fields without changing values
    // Also skip if zipState.data.number was never set (data loaded from database, user just navigating)
    const savedNumber = String(zipState?.data?.number ?? '');
    const hasNumberInState = zipState?.data?.number !== undefined;
    if (hasNumberInState && incoming === savedNumber) {
      return;
    }
    // If number was never saved to state but user is just blurring without changing,
    // skip GPS update (this handles data loaded from database)
    if (!hasNumberInState && !zipState?.lastSearchFound && !zipState?.apiFailure) {
      return;
    }

    // Create hash with current data to compare with last GPS search
    // This prevents unnecessary API calls and handles race conditions
    const savedAddress = zipState?.data || {};
    const currentGpsHash = `${savedAddress.zip || ''}|${savedAddress.address1 || ''}|${incoming}`;
    const lastGpsSearched = zipState?.lastGpsSearched;

    // If GPS was already searched with this exact hash, skip
    if (currentGpsHash === lastGpsSearched && savedAddress?.addressCoordinates?.length > 0) {
      return;
    }

    // Save number to state first
    const set = state => setFormZipState(zipFlattenFieldName, state);
    const updatedData = {
      ...zipState?.data,
      number: incoming,
    };
    set({ data: updatedData });

    // Update coordinates immediately on blur
    await updateGpsCoordinates(zipFlattenFieldName, updatedData, {
      getFormZipState,
      setFormZipState,
      setWorking,
      addressService,
      form,
      pendingGpsUpdateRef,
    });
  };

  /**
   * Handle address field change with debounce
   * Updates GPS coordinates after 2 seconds of inactivity
   * Used for manual address entry (API failure or unique ZIP)
   */
  const handleAddressChange = ({
    ev,
    value,
    flattenParentFieldName,
    fullParentFieldName,

    zip: { flattenFieldName: zipFlattenFieldName },
    address: { flattenFieldName: address1FlattenFieldName },
    number: { flattenFieldName: numberFlattenFieldName },
    city: { flattenFieldName: cityFlattenFieldName },
    region: { flattenFieldName: regionFlattenFieldName },
  }) => {
    const incoming = String(value ?? '');
    // Use the same key that handleAddressZipBlur uses to save state
    const zipState = getFormZipState(zipFlattenFieldName);
    const current = String(zipState?.data?.address1 ?? '');

    // Skip if apiFailure is not set (address should come from ZIP)
    if (!zipState?.apiFailure && !zipState?.zipWithMultipleAddress) {
      return;
    }

    // If address hasn't changed, cancel debounce
    if (incoming === current) {
      if (coordinateTimerRef.current) {
        clearTimeout(coordinateTimerRef.current);
        coordinateTimerRef.current = null;
      }
      return;
    }

    // Cancel previous debounce
    if (coordinateTimerRef.current) {
      clearTimeout(coordinateTimerRef.current);
      coordinateTimerRef.current = null;
    }

    // Start 2-second debounce to save state and update coordinates
    coordinateTimerRef.current = setTimeout(async () => {
      const set = state => setFormZipState(zipFlattenFieldName, state);
      const cityValue = form.getFieldValue(cityFlattenFieldName?.split('.'));
      const regionValue = form.getFieldValue(regionFlattenFieldName?.split('.'));
      const numberValue = form.getFieldValue(numberFlattenFieldName?.split('.'));

      const updatedAddressData = {
        ...zipState?.data,
        address1: incoming,
        city: cityValue,
        region: regionValue,
        number: numberValue,
      };
      set({ data: updatedAddressData });

      await updateGpsCoordinates(zipFlattenFieldName, updatedAddressData, {
        getFormZipState,
        setFormZipState,
        setWorking,
        addressService,
        form,
        pendingGpsUpdateRef,
      });
    }, 2000);
  };

  /**
   * Handle address field blur
   * Updates GPS coordinates immediately if address changed
   * Used for manual address entry (API failure or unique ZIP)
   */
  const handleAddressBlur = async ({
    ev,
    value,
    flattenParentFieldName,
    fullParentFieldName,

    zip: { flattenFieldName: zipFlattenFieldName },
    address: { flattenFieldName: address1FlattenFieldName },
    number: { flattenFieldName: numberFlattenFieldName },
    city: { flattenFieldName: cityFlattenFieldName },
    region: { flattenFieldName: regionFlattenFieldName },
  }) => {
    const incoming = String(value ?? '');
    // Use the same key that handleAddressZipBlur uses to save state
    const zipState = getFormZipState(zipFlattenFieldName);
    const current = String(zipState?.data?.address1 ?? '');

    // Cancel any pending debounce
    if (coordinateTimerRef.current) {
      clearTimeout(coordinateTimerRef.current);
      coordinateTimerRef.current = null;
    }
    // Skip if apiFailure is not set (address should come from ZIP)
    if (!zipState?.apiFailure && !zipState?.zipWithMultipleAddress) {
      return;
    }

    // If address hasn't changed, skip
    if (incoming === current) {
      return;
    }

    // Save address data to state first (before GPS update)
    const set = state => setFormZipState(zipFlattenFieldName, state);
    const cityValue = form.getFieldValue(cityFlattenFieldName?.split('.'));
    const regionValue = form.getFieldValue(regionFlattenFieldName?.split('.'));
    const numberValue = form.getFieldValue(numberFlattenFieldName?.split('.'));

    const updatedAddressData = {
      ...zipState?.data,
      address1: incoming,
      city: cityValue,
      region: regionValue,
      number: numberValue,
    };
    set({ data: updatedAddressData });

    // Update coordinates immediately on blur
    await updateGpsCoordinates(zipFlattenFieldName, updatedAddressData, {
      getFormZipState,
      setFormZipState,
      setWorking,
      addressService,
      form,
      pendingGpsUpdateRef,
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // PERFORMANCE FIX: Split exposedValues into separate memos to prevent cascading re-renders
  // Only form state values that change frequently are memoized separately
  const stableExposedValues = useMemo(
    () => ({
      form,
      elRefs,
      goFocus,
      setWatchFields,
      setFieldValidationStatus,
      requiredFields,
      requiredFieldsAtLeads,
      disabledFields,
      initialValues,
      setAdditionalFields,
      validateForm,
      resetForm: resetFormProxy,
      submitForm: submitFormProxy,
      gotoInitFocus,
      initFocus, // expose to allow child components to check if parent has custom initFocus

      // cnpj
      getFormCnpjState,
      setFormCnpjState,
      handleCnpjBlur,
      handleCnpjChange,

      // unique entity
      setUniqueEntity,
      getUniqueEntity,
      reloadEntirePage, // expose for SimpleCnpj unique handling

      // cpf
      getFormCpfState,
      setFormCpfState,
      handleCpfBlur,
      handleCpfChange,

      // zip
      getFormZipState,
      setFormZipState,
      handleAddressZipBlur,
      handleAddressZipChange,
      handleAddressNumberBlur,
      handleAddressNumberChange,
      handleAddressBlur,
      handleAddressChange,
      formatAddress,

      // validation errors
      removeFromFieldsWithError,
      addFieldsWithError,
    }),
    [
      form,
      requiredFields,
      requiredFieldsAtLeads,
      disabledFields,
      initialValues,
      submitFormProxy,
      resetFormProxy,
      goFocus,
      setUniqueEntity,
      getUniqueEntity,
      reloadEntirePage,
      initFocus,
    ]
  );

  const dynamicExposedValues = useMemo(
    () => ({
      data: data,
      locked,
      document,
      setDocument,
      additionalFields,
      submitting,
      submitted,
      working,
      setWorking,
      hasError,
      shouldShowErrors: hasError && !isUpdating,
      isUpdating,
      watches: watchedValues,
      fieldsWithError,
      // searchingCnpj,
      updatingCoordinates,
      pendingAddressNumber: waitingForAddressNumber,
      uniqueEntity,
      formTmpState, // expose to trigger re-render when cnpj/zip state changes
    }),
    [
      data,
      locked,
      document,
      additionalFields,
      submitting,
      submitted,
      working,
      hasError,
      isUpdating,
      watchedValues,
      fieldsWithError,
      // searchingCnpj,
      updatingCoordinates,
      formTmpState,
      waitingForAddressNumber,
      uniqueEntity,
    ]
  );

  const exposedValues = useMemo(
    () => ({
      ...stableExposedValues,
      ...dynamicExposedValues,
    }),
    [stableExposedValues, dynamicExposedValues]
  );

  // Keep a ref to the latest exposedValues to avoid infinite loops
  // The ref is updated synchronously on every render
  const exposedValuesRef = useRef(exposedValues);
  exposedValuesRef.current = exposedValues;

  // Register form context in ModuleProvider
  // IMPORTANT: We intentionally do NOT include exposedValues in dependencies
  // to prevent infinite loops. The registration happens once on mount,
  // and consumers should use useSimpleFormContext() for reactive updates.
  // The Zustand store is updated separately for debugging purposes.
  useEffect(() => {
    registerProviderContext?.('formContext', exposedValuesRef.current);

    return () => {
      registerProviderContext?.('formContext', null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerProviderContext]);

  // Re-register when `locked` changes so consumers outside the SimpleFormProvider tree
  // (e.g., ActionButton in PageHeader) see the updated locked state.
  // Without this, moduleContext.formContext stays stale and the save button remains disabled
  // after a module-level lock change (e.g., CRM's effectiveViewMode toggle on attendance start).
  useEffect(() => {
    if (!registerProviderContext) return;
    registerProviderContext('formContext', exposedValuesRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked]);

  // Separate effect to sync with Zustand for debugging (without causing re-registration)
  // NOTE: We only sync lightweight values to avoid Redux DevTools serialization issues
  // The full exposedValues includes the Ant Design form object which is too large
  useEffect(() => {
    // Only sync the values that are actually used from Zustand (goFocus in SignUp/ResetPassword)
    const lightweightValues = {
      goFocus,
      locked,
      submitting,
      submitted,
      working,
      hasError,
    };
    setGlobalStore?.('formProvider', lightweightValues);
  }, [goFocus, locked, submitting, submitted, working, hasError, setGlobalStore]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <SimpleFormContext.Provider value={exposedValues}>
        <Form
          noValidate
          key={formKey}
          form={form}
          name={name}
          onFinish={submitForm}
          onFinishFailed={handleFinishErrorInternal}
          validateTrigger={formValidateTrigger}
          component={useInModal ? false : 'form'}
          onValuesChange={(changed, allValues) => {
            // Call external callback if provided
            externalOnValuesChange?.(changed, allValues);

            // PERFORMANCE FIX: Early return if no watched fields
            if (!watchedFields.length) return;

            // PERFORMANCE FIX: Only flatten CHANGED fields (not all values)
            const flatChanged = flattenObject(changed);
            const changedKeys = Object.keys(flatChanged);

            // PERFORMANCE FIX: Quick check - if no changed keys, skip
            if (!changedKeys.length) return;

            // PERFORMANCE FIX: Use memoized Set for O(1) lookup (watchedFieldsSet is defined above)
            const changedWatchedFields = changedKeys.filter(key => {
              // Exact match
              if (watchedFieldsSet.has(key)) return true;
              // Nested match (e.g., 'phone.value' matches 'phone')
              return watchedFields.some(wf => key.startsWith(`${wf}.`));
            });

            // PERFORMANCE FIX: Only update if a watched field actually changed
            if (!changedWatchedFields.length) return;

            // PERFORMANCE FIX: Only get values for the CHANGED watched fields, not ALL values
            // This avoids the expensive flattenObject(allValues) on every keystroke
            setWatchedValues(prev => {
              const updated = { ...prev };
              let hasChanges = false;

              changedWatchedFields.forEach(key => {
                const newValue = flatChanged[key];
                if (prev[key] !== newValue) {
                  updated[key] = newValue;
                  hasChanges = true;
                }
              });

              return hasChanges ? updated : prev;
            });
          }}
          layout={layout}
          style={{ width: '100%', ...style }}
          onKeyDown={ev => {
            if (useInModal && ev.key === 'Enter') {
              ev.preventDefault();
            }
          }}
          initialValues={initialValues}
          {...props}
        >
          {children}

          {showSubmitButton && (
            <Flex justify="end" style={{ marginTop: 16 }}>
              <Form.Item>
                <Button
                  type="primary"
                  onClick={submitForm}
                  loading={submitting}
                  disabled={locked || working || submitting}
                  style={styleSubmitButton}
                >
                  {translateX('save')}
                </Button>
              </Form.Item>
            </Flex>
          )}
        </Form>
      </SimpleFormContext.Provider>
      {contextModal}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleFormProvider;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function flattenObject(obj, prefix = '', out = {}) {
  if (obj == null || typeof obj !== 'object') {
    out[prefix] = obj;
    return out;
  }
  const isArray = Array.isArray(obj);
  Object.keys(obj).forEach(key => {
    const val = obj[key];
    const nextPrefix =
      prefix === '' ? String(key) : isArray ? `${prefix}[${key}]` : `${prefix}.${key}`;
    if (val != null && typeof val === 'object') {
      flattenObject(val, nextPrefix, out);
    } else {
      out[nextPrefix] = val;
    }
  });
  return out;
}

/**
 * Get only the changed fields by comparing current values with initial values
 * @param {object} currentValues - Current form values
 * @param {object} initialValues - Initial form values
 * @returns {object} Object containing only changed fields (nested structure preserved)
 */
function getChangedFields(currentValues, initialValues) {
  const flatCurrent = flattenObject(currentValues);
  const flatInitial = flattenObject(initialValues);

  const changedKeys = Object.keys(flatCurrent).filter(key => {
    const currentVal = flatCurrent[key];
    const initialVal = flatInitial[key];

    // Handle undefined/null comparisons
    if (currentVal === initialVal) return false;
    if (currentVal == null && initialVal == null) return false;

    // Ignore changes from undefined/null to empty string
    if ((initialVal === undefined || initialVal === null) && currentVal === '') return false;

    // Deep comparison for objects/arrays (already flattened, so direct comparison works)
    return JSON.stringify(currentVal) !== JSON.stringify(initialVal);
  });

  // Rebuild nested structure from flat keys
  const changed = {};
  changedKeys.forEach(key => {
    const value = flatCurrent[key];
    const parts = key.split(/\.|\[|\]/).filter(Boolean);

    let current = changed;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      const nextPart = parts[i + 1];

      // Determine if next level should be array or object
      const isNextArray = !isNaN(parseInt(nextPart));

      if (!current[part]) {
        current[part] = isNextArray ? [] : {};
      }
      current = current[part];
    }

    const lastPart = parts[parts.length - 1];
    current[lastPart] = value;
  });

  // Clean up empty nested objects
  return cleanEmptyObjects(changed);
}

/**
 * Recursively removes empty objects and arrays from nested structure
 * IMPORTANT: Keeps empty strings ('') because they represent user clearing a field
 * @param {*} obj - Object to clean
 * @returns {*} Cleaned object without empty nested structures
 */
function cleanEmptyObjects(obj) {
  if (obj === null || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    const cleaned = obj.map(cleanEmptyObjects).filter(item => {
      if (item === null || item === undefined) return false;
      if (typeof item === 'object' && Object.keys(item).length === 0) return false;
      return true;
    });
    return cleaned.length > 0 ? cleaned : undefined;
  }

  const cleaned = {};
  for (const key in obj) {
    const value = cleanEmptyObjects(obj[key]);

    // Skip undefined, null, empty objects, and empty arrays
    // IMPORTANT: DO NOT skip empty strings ('') - they represent user clearing a field
    if (value === undefined) continue;
    if (value === null) continue;
    // if (value === '') continue;  // REMOVED: Keep empty strings to send field deletions to backend
    if (typeof value === 'object' && Object.keys(value).length === 0) continue;

    cleaned[key] = value;
  }

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

/**
 * Merges two objects, preserving user-entered data
 * Only overwrites fields from current that are empty/null/undefined with values from incoming
 * @param {object} current - Current form values (user data to preserve)
 * @param {object} incoming - Incoming data from API (to fill empty fields)
 * @returns {object} Merged object preserving user data
 */
function mergePreservingUserData(current, incoming) {
  const result = { ...current };

  for (const key in incoming) {
    const currentValue = current[key];
    const incomingValue = incoming[key];

    // If current value is empty/null/undefined, use incoming value
    if (currentValue === null || currentValue === undefined || currentValue === '') {
      result[key] = incomingValue;
    }
    // If both are objects (not arrays), merge recursively
    else if (
      typeof currentValue === 'object' &&
      !Array.isArray(currentValue) &&
      typeof incomingValue === 'object' &&
      !Array.isArray(incomingValue)
    ) {
      result[key] = mergePreservingUserData(currentValue, incomingValue);
    }
    // Otherwise, keep current value (preserve user data)
  }

  return result;
}

/**
 * Updates GPS coordinates based on current address data
 * Helper function that receives all React component dependencies through options
 *
 * @param {string} zipFlattenFieldNameParent - State key for ZIP (e.g., 'zip', 'address.zip', 'moreAddress[0].zip')
 * @param {Object} addressData - Address data to geocode (address1, number, city, region, zip)
 * @param {Object} options - Component dependencies and additional options
 * @param {Function} options.getFormZipState - Function to get zipState from formZipStates ref
 * @param {Function} options.setFormZipState - Function to set zipState in formZipStates ref
 * @param {Function} options.setWorking - Function to set working state (loading indicator)
 * @param {Object} options.addressService - Address service with getGpsCoordinates method
 * @param {Object} options.form - Ant Design form instance
 * @param {Object} options.pendingGpsUpdateRef - Ref for pending GPS updates queue
 * @param {string} [options.lastSearchFound] - Optional: lastSearchFound value to preserve in state
 */
async function updateGpsCoordinates(zipFlattenFieldNameParent, addressData = {}, options = {}) {
  // ─────────────────────────────────────────────────────────────────────────────
  // Destructure all component dependencies from options
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    getFormZipState,
    setFormZipState,
    setWorking,
    addressService,
    form,
    pendingGpsUpdateRef,
    lastSearchFound: lastSearchFoundOption,
  } = options;

  // ─────────────────────────────────────────────────────────────────────────────
  // Get current zip state and create setter
  // ─────────────────────────────────────────────────────────────────────────────
  const zipState = getFormZipState(zipFlattenFieldNameParent);
  const set = state => setFormZipState(zipFlattenFieldNameParent, state);

  // ─────────────────────────────────────────────────────────────────────────────
  // Extract address fields from provided data or existing state
  // ─────────────────────────────────────────────────────────────────────────────
  const savedAddress = zipState?.data || {};
  const {
    address1 = savedAddress.address1,
    number = savedAddress.number,
    city = savedAddress.city,
    region = savedAddress.region,
    zip = savedAddress.zip,
  } = addressData;

  // ─────────────────────────────────────────────────────────────────────────────
  // Validation: Skip if no address data
  // ─────────────────────────────────────────────────────────────────────────────
  if (!address1 && !city) {
    return;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Deduplication: Create unique hash for this GPS search
  // ─────────────────────────────────────────────────────────────────────────────
  const currentGpsHash = `${zip || ''}|${address1 || ''}|${number || ''}`;
  const lastGpsSearched = zipState?.lastGpsSearched;

  // Skip if we already searched for these exact coordinates
  if (currentGpsHash === lastGpsSearched && savedAddress?.addressCoordinates?.length > 0) {
    return;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Start GPS search process
  // ─────────────────────────────────────────────────────────────────────────────
  setWorking(true);
  set({ updatingCoordinates: true });

  // Call GPS geocoding service
  const result = await addressService.getGpsCoordinates({
    address: address1,
    number,
    city,
    region,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Handle GPS result
  // ─────────────────────────────────────────────────────────────────────────────
  if (result.case === 3) {
    // SUCCESS: GPS coordinates found
    const coordinates = result.data; // [lat, lng]

    // Update state with all address fields + coordinates + hash
    const updatedData = {
      ...savedAddress,
      address1,
      city,
      region,
      number,
      addressCoordinates: coordinates,
    };

    // FIX: Don't spread ...zipState here - it's captured at function start and may be stale
    // (e.g., searching: true when handleAddressZipBlur already set it to false)
    // setFormZipState already merges with existing state, so we only set what we need to update
    set({
      updatingCoordinates: false,
      data: updatedData,
      lastGpsSearched: currentGpsHash, // Save hash to prevent duplicate searches
      ...(lastSearchFoundOption && { lastSearchFound: lastSearchFoundOption }), // Preserve lastSearchFound from CEP if provided
    });

    // ───────────────────────────────────────────────────────────────────────────
    // Update form coordinates based on field structure
    // ───────────────────────────────────────────────────────────────────────────
    // Remove '.zip' suffix to get the parent field name (e.g., 'address.zip' -> 'address')
    const parentFieldName = zipFlattenFieldNameParent?.replace(/\.zip$/, '') || '';

    if (parentFieldName && parentFieldName !== 'zip') {
      // SUBDOCUMENT CASE (e.g., 'address', 'moreAddress[0]')
      const coordsFieldName = `${parentFieldName}.addressCoordinates`;
      const numberFieldName = `${parentFieldName}.number`;

      // Convert array notation to object path for setFieldsValue
      const coordsPath = coordsFieldName.replace(/\[(\d+)\]/g, '.$1');
      const numberPath = numberFieldName.replace(/\[(\d+)\]/g, '.$1');

      // Build nested object helper
      const buildNestedObject = (path, value) => {
        const parts = path.split('.');
        let obj = value;
        for (let i = parts.length - 1; i >= 0; i--) {
          obj = { [parts[i]]: obj };
        }
        return obj;
      };

      const coordsUpdate = buildNestedObject(coordsPath, coordinates);
      const numberUpdate = buildNestedObject(numberPath, number);

      // Deep merge helper
      const mergeDeep = (target, source) => {
        for (const key in source) {
          if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            target[key] = mergeDeep(target[key] || {}, source[key]);
          } else {
            target[key] = source[key];
          }
        }
        return target;
      };

      const finalUpdate = mergeDeep(coordsUpdate, numberUpdate);
      form.setFieldsValue(finalUpdate);
    } else {
      // ROOT LEVEL CASE
      form.setFieldsValue({
        addressCoordinates: coordinates,
        number,
      });
    }
  } else {
    // FAILURE: GPS not found - still save the address data without coordinates
    const updatedData = {
      ...savedAddress,
      address1,
      city,
      region,
      number,
    };

    // FIX: Don't spread ...zipState - same stale state issue as success case
    set({
      updatingCoordinates: false,
      data: updatedData,
      ...(lastSearchFoundOption && { lastSearchFound: lastSearchFoundOption }),
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Cleanup: Stop loading indicator
  // ─────────────────────────────────────────────────────────────────────────────
  setWorking(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // Enfileiramento: Check if there's a pending GPS update queued
  // ─────────────────────────────────────────────────────────────────────────────
  // This happens when user types number during CEP search (race condition handling)
  if (pendingGpsUpdateRef.current) {
    const pending = pendingGpsUpdateRef.current;
    pendingGpsUpdateRef.current = null; // Clear queue before executing

    // Execute the pending update recursively
    await updateGpsCoordinates(pending.zipFlattenFieldNameParent, pending.addressData, options);
  }
}

/**
 * Merges address data when user changes CEP in existing document
 * Replaces address-related fields but preserves user-specific fields (number, address2, addressAdditionalInfo)
 *
 * @param {object} current - Current form values
 * @param {object} incoming - Incoming address data from CEP API
 * @returns {object} Merged object with replaced address fields
 */
function mergeReplacingAddressData(current, incoming) {
  const result = { ...current };

  // Fields that should be REPLACED when CEP changes
  const replaceFields = [
    'zip',
    'address1',
    'city',
    'region',
    'neighborhood',
    'addressCoordinates',
    'addressMetadata',
    '__city',
  ];

  // Fields that should be PRESERVED (user-specific data)
  const preserveFields = ['number', 'address2', 'addressTitle', 'addressAdditionalInfo'];

  for (const key in incoming) {
    const incomingValue = incoming[key];

    // Replace address fields with new CEP data
    if (replaceFields.includes(key)) {
      result[key] = incomingValue;
    }
    // Preserve user-specific fields (only fill if empty)
    else if (preserveFields.includes(key)) {
      const currentValue = current[key];
      if (currentValue === null || currentValue === undefined || currentValue === '') {
        result[key] = incomingValue;
      }
      // Otherwise keep current value (preserve user data)
    }
    // For nested objects, merge recursively
    else if (
      typeof incomingValue === 'object' &&
      !Array.isArray(incomingValue) &&
      current[key] &&
      typeof current[key] === 'object' &&
      !Array.isArray(current[key])
    ) {
      result[key] = mergeReplacingAddressData(current[key], incomingValue);
    }
    // For all other fields, use incoming value
    else {
      result[key] = incomingValue;
    }
  }

  return result;
}
